// Why T-MOS Content Management Tab
// Date: January 27, 2025

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contentManagementService, WhyTMOSContent } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';

const SECTION_KEYS: Array<{ key: WhyTMOSContent['tab_key']; label: string; description: string }> = [
  { key: 'tmos_difference', label: 'The T-MOS Difference', description: 'Main comparison section showing T-MOS vs traditional approaches' },
  { key: 'tmos_solutions', label: 'Why T-MOS Succeeds', description: 'Section explaining how T-MOS addresses transformation challenges' },
  { key: 'tmos_unique_features', label: 'What Makes T-MOS Unique', description: 'Section highlighting T-MOS unique features and differentiators' },
];

export const WhyTMOSTab: React.FC = () => {
  const [tabs, setTabs] = useState<WhyTMOSContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<WhyTMOSContent['tab_key']>('tmos_difference');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    loadTabs();
  }, []);

  useEffect(() => {
    const tab = tabs.find(t => t.tab_key === activeTab);
    if (tab) {
      setContent(tab.content_html);
      setTitle(tab.title);
      setDescription(tab.description || '');
    } else {
      setContent('');
      setTitle('');
      setDescription('');
    }
  }, [activeTab, tabs]);

  const loadTabs = async () => {
    try {
      setLoading(true);
      const data = await contentManagementService.getAllWhyTMOSTabs();
      setTabs(data);
    } catch (error: any) {
      console.error('Error loading Why T-MOS tabs:', error);
      toast.error(error.response?.data?.message || 'Failed to load Why T-MOS content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim() || !title.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      setSaving(true);
      await contentManagementService.updateWhyTMOSTab(activeTab, {
        title: title,
        description: description || undefined,
        content_html: content,
      });
      toast.success('Why T-MOS content saved successfully');
      await loadTabs();
    } catch (error: any) {
      console.error('Error saving Why T-MOS content:', error);
      toast.error(error.response?.data?.message || 'Failed to save Why T-MOS content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-[var(--color-border)]">
        {SECTION_KEYS.map((section) => (
          <button
            key={section.key}
            onClick={() => setActiveTab(section.key)}
            className={`
              px-4 py-2 text-sm font-medium border-b-2 transition-colors
              ${activeTab === section.key
                ? 'border-[var(--color-accent-bg)] text-[var(--color-surface-text)] font-semibold'
                : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]'
              }
            `}
          >
            {section.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit {SECTION_KEYS.find(s => s.key === activeTab)?.label} Content</CardTitle>
          {SECTION_KEYS.find(s => s.key === activeTab)?.description && (
            <p className="text-sm text-[var(--color-secondary-text)] mt-1">
              {SECTION_KEYS.find(s => s.key === activeTab)?.description}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Content (HTML) *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] font-mono text-sm"
              placeholder="Enter HTML content..."
              required
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            {saving ? 'Saving...' : 'Save Content'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

